/**
 * toast — lightweight, non-blocking notifications for smooth feedback across
 * flows (creation started, asset ready, errors). Unlike the dialogs in
 * dialogs.tsx (which block for a decision), toasts are transient and stack.
 *
 *   toast.success('Your avatar is ready!');
 *   toast.info('Creating your avatar — it updates here automatically.');
 *   toast.error('Upload failed — please try again.');
 *
 * A single host is mounted on document.body on first use (no provider needed).
 */
import { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { CheckCircle2, Info, AlertTriangle, X } from 'lucide-react';
import { cn } from '@/v2/utils';

type ToastType = 'success' | 'info' | 'error';
interface ToastItem { id: number; message: string; type: ToastType; duration: number }

let pushFn: ((t: ToastItem) => void) | null = null;
let queue: ToastItem[] = [];
let nextId = 1;
let hostMounted = false;

function ToastView({ item, onClose }: { item: ToastItem; onClose: () => void }) {
  const Icon = item.type === 'success' ? CheckCircle2 : item.type === 'error' ? AlertTriangle : Info;
  const tone = item.type === 'success' ? 'text-primary' : item.type === 'error' ? 'text-amber-400' : 'text-primary';
  return (
    <div className="pointer-events-auto flex items-center gap-3 pl-4 pr-3 py-3 rounded-2xl bg-[#15151d]/95 backdrop-blur-md border border-white/10 shadow-2xl animate-slide-up max-w-md">
      <Icon className={cn('w-5 h-5 shrink-0', tone)} />
      <span className="text-sm text-text-primary leading-snug">{item.message}</span>
      <button onClick={onClose} className="ml-1 w-6 h-6 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-primary hover:bg-white/10 transition-colors shrink-0">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    const remove = (id: number) => setItems((prev) => prev.filter((x) => x.id !== id));
    pushFn = (t) => {
      setItems((prev) => [...prev, t]);
      setTimeout(() => remove(t.id), t.duration);
    };
    if (queue.length) { queue.forEach((t) => pushFn!(t)); queue = []; }
    return () => { pushFn = null; };
  }, []);
  return (
    <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[140] flex flex-col items-center gap-2 pointer-events-none px-4">
      {items.map((t) => (
        <ToastView key={t.id} item={t} onClose={() => setItems((prev) => prev.filter((x) => x.id !== t.id))} />
      ))}
    </div>
  );
}

function ensureHost() {
  if (hostMounted) return;
  hostMounted = true;
  const el = document.createElement('div');
  document.body.appendChild(el);
  createRoot(el).render(<ToastHost />);
}

function show(message: string, type: ToastType, duration: number) {
  ensureHost();
  const item: ToastItem = { id: nextId++, message, type, duration };
  if (pushFn) pushFn(item);
  else queue.push(item); // host still mounting — flushed when its effect registers
}

export const toast = {
  success: (message: string, duration = 4000) => show(message, 'success', duration),
  info: (message: string, duration = 4000) => show(message, 'info', duration),
  error: (message: string, duration = 5000) => show(message, 'error', duration),
};
